import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/AdminLayout";
import { useAuth } from "@/context/AuthContext";
import { reviewAPI } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Check, Trash2, Loader2, Star, Clock, CheckCircle } from "lucide-react";

const AdminReviews = () => {
  const { token } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    try {
      const response = await reviewAPI.getAllAdmin(token);
      setReviews(response.data);
    } catch (error) {
      toast.error("Failed to fetch reviews");
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (id) => {
    try {
      await reviewAPI.approve(id, token);
      toast.success("Review approved");
      fetchReviews();
    } catch (error) {
      toast.error("Failed to approve review");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this review?")) return;

    try {
      await reviewAPI.delete(id, token);
      toast.success("Review deleted");
      fetchReviews();
    } catch (error) {
      toast.error("Failed to delete review");
    }
  };

  const filteredReviews = reviews.filter((review) => {
    if (filter === "pending") return !review.is_approved;
    if (filter === "approved") return review.is_approved;
    return true;
  });

  const pendingCount = reviews.filter((r) => !r.is_approved).length;

  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-reviews">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1
              className="text-3xl font-semibold text-foreground mb-2"
             
            >
              Reviews
            </h1>
            <p className="text-muted-foreground">
              Manage and moderate guest reviews
            </p>
          </div>

          {/* Filter */}
          <div className="flex gap-2">
            <Button
              variant={filter === "all" ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter("all")}
              className="rounded-full"
              data-testid="filter-all"
            >
              All ({reviews.length})
            </Button>
            <Button
              variant={filter === "pending" ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter("pending")}
              className="rounded-full"
              data-testid="filter-pending"
            >
              <Clock className="w-4 h-4 mr-1" />
              Pending ({pendingCount})
            </Button>
            <Button
              variant={filter === "approved" ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter("approved")}
              className="rounded-full"
              data-testid="filter-approved"
            >
              <CheckCircle className="w-4 h-4 mr-1" />
              Approved
            </Button>
          </div>
        </div>

        {/* Reviews List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : filteredReviews.length > 0 ? (
          <div className="space-y-4">
            {filteredReviews.map((review) => (
              <div
                key={review.id}
                className="bg-white rounded-2xl p-6 shadow-soft"
                data-testid={`review-item-${review.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-3">
                      <span className="font-semibold text-foreground">
                        {review.reviewer_name}
                      </span>
                      <Badge
                        variant={review.is_approved ? "default" : "secondary"}
                        className={review.is_approved ? "bg-green-100 text-green-700" : ""}
                      >
                        {review.is_approved ? "Approved" : "Pending"}
                      </Badge>
                    </div>

                    <div className="flex items-center gap-1 mb-3">
                      {[...Array(5)].map((_, i) => (
                        <Star
                          key={i}
                          className={`w-4 h-4 ${
                            i < review.rating
                              ? "fill-accent text-accent"
                              : "text-gray-300"
                          }`}
                        />
                      ))}
                    </div>

                    <p className="text-muted-foreground mb-3">{review.comment}</p>

                    <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                      <span>Email: {review.reviewer_email}</span>
                      <span>
                        {new Date(review.created_at).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    {!review.is_approved && (
                      <Button
                        size="sm"
                        onClick={() => handleApprove(review.id)}
                        className="bg-green-600 hover:bg-green-700"
                        data-testid={`approve-review-${review.id}`}
                      >
                        <Check className="w-4 h-4 mr-1" />
                        Approve
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDelete(review.id)}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      data-testid={`delete-review-${review.id}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Delete
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-12 shadow-soft text-center">
            <Star className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-foreground mb-2">
              No Reviews Found
            </h3>
            <p className="text-muted-foreground">
              {filter === "pending"
                ? "All reviews have been approved!"
                : "Reviews will appear here once guests submit them."}
            </p>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminReviews;
